export interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

export type ChatEvent =
  | { type: 'text'; text: string }
  | { type: 'thinking' }
  | { type: 'tool'; name: string; summary: string }
  | { type: 'tool_result'; name: string; ok: boolean }
  | { type: 'done' }
  | { type: 'error'; message: string };

/**
 * POST to the assistant SSE endpoint and yield parsed events as they stream.
 * (EventSource is GET-only, so we read the response body and parse SSE by hand.)
 */
export async function* streamChat(
  body: { message: string; history: ChatTurn[]; contextPath?: string },
  signal: AbortSignal,
): AsyncGenerator<ChatEvent> {
  const res = await fetch('/api/assistant/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    yield { type: 'error', message: `assistant request failed (${res.status})` };
    yield { type: 'done' };
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line.
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      try {
        yield JSON.parse(dataLine.slice(5).trim()) as ChatEvent;
      } catch {
        /* ignore malformed frame */
      }
    }
  }
}

export async function assistantAvailable(): Promise<boolean> {
  try {
    const r = await fetch('/api/assistant/status');
    if (!r.ok) return false;
    return (await r.json()).available === true;
  } catch {
    return false;
  }
}
